import { hasProfile, saveProfile, setLocalPhoto, getProfile, getLocalPhoto } from '../data/personalization';

/** Read + shrink a picked photo to a ~512px JPEG data-URL (kept on-device only). */
async function compressPhoto(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result as string);
    fr.onerror = () => rej(new Error('read'));
    fr.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = () => rej(new Error('img'));
    i.src = dataUrl;
  });
  const max = 512;
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.85);
}

const inputCss =
  'width:100%;box-sizing:border-box;margin:4px 0 10px;padding:8px;border-radius:8px;border:1px solid #5a6b58;background:#1c241d;color:#fff;font-family:monospace;font-size:14px;';
const labelCss = 'font-size:12px;color:#cfe0c4;';

function esc(s: string): string {
  return s.replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/** Show the setup form; resolves once saved. Used at first run and from Settings. */
export function showProfileForm(): Promise<void> {
  return new Promise((resolve) => {
    const p = getProfile();
    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:99999;background:rgba(20,28,22,.94);display:flex;align-items:center;justify-content:center;padding:16px;font-family:monospace;';
    const card = document.createElement('div');
    card.style.cssText =
      'background:#2d3b2f;border:2px solid #7dbf5a;border-radius:14px;max-width:340px;width:100%;padding:18px;color:#fff;box-sizing:border-box;max-height:92vh;overflow:auto;';
    card.innerHTML = `
      <h2 style="margin:0 0 4px;font-size:18px;text-align:center;">Set up your farm 💛</h2>
      <p style="margin:0 0 14px;font-size:11px;color:#bcd0b4;text-align:center;line-height:1.4;">These details stay only on this device.</p>
      <div style="${labelCss}">Farm name</div>
      <input id="pf-farm" value="${esc(p.farmName ?? '')}" placeholder="Sunny Acres" style="${inputCss}"/>
      <div style="${labelCss}">Your name</div>
      <input id="pf-name" value="${esc(p.playerDefaultName ?? '')}" placeholder="Your name" style="${inputCss}"/>
      <div style="${labelCss}">Little one's name</div>
      <input id="pf-clara" value="${esc(p.claraName ?? 'Clara')}" placeholder="Clara" style="${inputCss}"/>
      <div style="${labelCss}">Birthday (optional)</div>
      <div style="display:flex;gap:8px;">
        <input id="pf-bm" type="number" min="1" max="12" placeholder="Month" value="${p.birthday?.month ?? ''}" style="${inputCss}"/>
        <input id="pf-bd" type="number" min="1" max="31" placeholder="Day" value="${p.birthday?.day ?? ''}" style="${inputCss}"/>
      </div>
      <div style="${labelCss}">Photo (optional)</div>
      <input id="pf-photo" type="file" accept="image/*" style="font-size:12px;color:#cfe0c4;margin:4px 0 8px;width:100%;"/>
      <div id="pf-prev" style="text-align:center;margin-bottom:10px;"></div>
      <button id="pf-go" style="width:100%;padding:11px;border:none;border-radius:8px;background:#7dbf5a;color:#1c241d;font-family:monospace;font-size:16px;font-weight:bold;">Start playing ▶</button>
    `;
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    const $ = (id: string): HTMLInputElement => card.querySelector('#' + id) as HTMLInputElement;
    const prev = card.querySelector('#pf-prev') as HTMLDivElement;
    const existing = getLocalPhoto();
    if (existing) prev.innerHTML = `<img src="${existing}" style="max-height:84px;border-radius:8px;border:2px solid #f4ecdc;"/>`;

    let photoData: string | null = null;
    $('pf-photo').onchange = () => {
      const f = $('pf-photo').files?.[0];
      if (!f) return;
      compressPhoto(f)
        .then((d) => {
          photoData = d;
          prev.innerHTML = `<img src="${d}" style="max-height:84px;border-radius:8px;border:2px solid #f4ecdc;"/>`;
        })
        .catch(() => {
          /* ignore a bad image */
        });
    };

    (card.querySelector('#pf-go') as HTMLButtonElement).onclick = () => {
      const bm = parseInt($('pf-bm').value, 10);
      const bd = parseInt($('pf-bd').value, 10);
      const birthday = bm >= 1 && bm <= 12 && bd >= 1 && bd <= 31 ? { month: bm, day: bd } : null;
      saveProfile({
        farmName: $('pf-farm').value.trim() || 'Sunny Acres',
        playerDefaultName: $('pf-name').value.trim() || 'Friend',
        claraName: $('pf-clara').value.trim() || 'Clara',
        birthday,
      });
      if (photoData) setLocalPhoto(photoData);
      overlay.remove();
      resolve();
    };
  });
}

/** First run only: capture the profile before the game boots. */
export async function runOnboardingIfNeeded(): Promise<void> {
  if (hasProfile()) return;
  await showProfileForm();
}
